import { AxiosResponse } from 'axios';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  Configuration,
  PmsApi,
  UpdateCompanyOnboardingDTO,
  CreateCompanyOnboardingDTO,
  CompanyOnboardingDTO,
  AcceptTermsandConditionsDto,
  TermsAndConditionsDto,
  CompanyStaffDto,
  LegalApi,
  AuthApi,
  ProductTypeEnum,
  ProductDto,
  CompaniesApi,
  ProductsPackagesV2Dto,
  UpsertPricingQuoteDto,
  PricingQuoteDto,
  TieredCompanyPricingOptionDTO,
} from '@inosio/payments-sdk';
import { selectCurrentUser, IpaUserExtended } from '../../../shared/sagas/auth/authSlice';
import {
  getOnboardingContractRateOptions,
  getRecordAgreementDetails,
  getStartOnboardingApplication,
  getInosioTermsAndConditions,
  getUpdateOnboardingApplication,
  setOnboardingApplicationError,
  setOnboardingContractRateOptions,
  setRecordAgreementDetailsSuccess,
  setRecordAgreementDetailsFailed,
  setStartOnboardingApplication,
  setInosioTermAndConditions,
  setUpdateOnboardingApplication,
  getPaysafeCCTermsAndConditions,
  setPaysafeCCTermAndConditions,
  setPmDetails,
  setPmDetailsError,
  getPmDetails,
  updateOnboardingDto,
  getInosioPrivacyPolicy,
  setInosioPrivacyPolicy,
  setProducts,
  getProducts,
  batchRecordAgreementDetails,
  fetchProductsPackagesV2,
  setProductsPackagesV2,
  setErrorFetchingProductsPackagesV2,
  createPricingQuote,
  setCreatePricingQuoteSuccess,
  setCreatePricingQuoteFailed,
  updatePricingQuote,
  setUpdatePricingQuoteSuccess,
  setUpdatePricingQuoteFailed,
  fetchPricingQuote,
  setFetchPricingQuoteSuccess,
  setFetchPricingQuoteFailed,
  downloadPricingQuote,
  setDownloadPricingQuoteSuccess,
  setDownloadPricingQuoteFailed,
} from './pmonboardingSlice';
import { Stream } from 'stream';

const { REACT_APP_IPA_BASE_PATH } = process.env;
const apiConfig = new Configuration({ basePath: REACT_APP_IPA_BASE_PATH });

export function requestDownloadPricingQuote(pricingQuoteUuid: string) {
  const responseType = 'arraybuffer';
  const contentDisposition = 'application/pdf';
  const headerParams = {
    'Content-Disposition': contentDisposition,
  };

  const ipaLegalApi = new LegalApi(apiConfig);
  return ipaLegalApi.downloadPricingQuoteDoc(pricingQuoteUuid, { responseType, headers: headerParams });
}

export function requestFetchPricingQuote(pricingQuoteUuid: string) {
  const ipaLegalApi = new LegalApi(apiConfig);
  return ipaLegalApi.getPricingQuote(pricingQuoteUuid);
}

export function requestUpdatePricingQuote(pricingQuoteUuid: string, upsertPricingQuoteDto: UpsertPricingQuoteDto) {
  const ipaLegalApi = new LegalApi(apiConfig);
  return ipaLegalApi.upsertPricingQuote(upsertPricingQuoteDto, pricingQuoteUuid);
}

export function requestCreatePricingQuote(upsertPricingQuoteDto: UpsertPricingQuoteDto) {
  const ipaLegalApi = new LegalApi(apiConfig);
  return ipaLegalApi.upsertPricingQuote(upsertPricingQuoteDto);
}

export function requestFetchProductsPackagesV2() {
  const ipaLegalAPI = new LegalApi(apiConfig);
  return ipaLegalAPI.getProductPackagesV2();
}

export function requestStartOnboardingApplication(access_token: string, createCompanyOnboardingDTO: CreateCompanyOnboardingDTO) {
  apiConfig.accessToken = access_token;
  const ipaCompaniesAPI = new CompaniesApi(apiConfig);
  return ipaCompaniesAPI.createOnboardingApplication(createCompanyOnboardingDTO);
}

export function requestInosioTermsAndConditions() {
  const ipaLegalApi = new LegalApi(apiConfig);
  return ipaLegalApi.getInosioPmTermsConditions();
}

export function requestInosioPrivacyPolicy() {
  const ipaLegalApi = new LegalApi(apiConfig);
  return ipaLegalApi.getInosioPrivacyPolicy();
}

export function requestProcessorTermsAndConditions(access_token: string, pmUuid: string) {
  apiConfig.accessToken = access_token;
  const ipaPmsApi = new PmsApi(apiConfig);
  return ipaPmsApi.getProcessorTermsConditions(pmUuid);
}

export function requestRecordAgreementDetails(access_token: string, acceptTermsAndConditionsDTO: AcceptTermsandConditionsDto) {
  apiConfig.accessToken = access_token;
  const ipaAuthAPI = new AuthApi(apiConfig);
  return ipaAuthAPI.acceptterms(acceptTermsAndConditionsDTO);
}

export function requestGetPmDetails(access_token: string, pmUUID: string) {
  apiConfig.accessToken = access_token;
  const ipaPmsAPI = new PmsApi(apiConfig);
  return ipaPmsAPI.findOnePM(pmUUID);
}

export function requestOnboardingContractRateOptions() {
  const ipaLegalAPI = new LegalApi(apiConfig);
  return ipaLegalAPI.getProductsPackages();
}

export function requestGetProducts(organizationUuid: string, productType?: ProductTypeEnum) {
  const ipaLegalAPI = new LegalApi(apiConfig);
  return ipaLegalAPI.getProducts(organizationUuid, productType);
}

export function requestUpdateOnboardingApplication(access_token: string, companyUUID: string, updateCompanyOnboardingDTO: UpdateCompanyOnboardingDTO) {
  const { REACT_APP_IPA_BASE_PATH } = process.env;
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken: access_token,
  });
  const ipaCompaniessApi = new CompaniesApi(apiConfig);

  return ipaCompaniessApi.updateOnboardingApplication(companyUUID, updateCompanyOnboardingDTO);
}

function* handleStartOnboardingApplication(action: { type: typeof getStartOnboardingApplication; payload: CreateCompanyOnboardingDTO }) {
  const onboardingDTO = action.payload as CreateCompanyOnboardingDTO;

  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);

    const response: AxiosResponse<UpdateCompanyOnboardingDTO> = yield call(requestStartOnboardingApplication, currentUser.accessToken, onboardingDTO);

    const { data } = response;

    yield put(setStartOnboardingApplication({ ...data }));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setOnboardingApplicationError({ ...error?.response?.data }));
    } else {
      yield put(setOnboardingApplicationError({ ...error }));
    }
  }
}

// shouldn't this be handleSubmit...?
function* handleUpdateOnboardingApplication(action: { type: typeof getUpdateOnboardingApplication; payload: { company_uuid: string; data: UpdateCompanyOnboardingDTO } }) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<CompanyOnboardingDTO> = yield call(requestUpdateOnboardingApplication, currentUser.accessToken, action.payload.company_uuid, action.payload.data);
    const { data } = response;
    yield put(setUpdateOnboardingApplication({ ...data }));
    yield put(updateOnboardingDto({ ...data }));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setOnboardingApplicationError({ ...error?.response?.data }));
    } else {
      yield put(setOnboardingApplicationError(error));
    }
  }
}

export function* handleGetOnboardingContractRateOptions(action: { type: typeof getOnboardingContractRateOptions }) {
  try {
    const response: AxiosResponse<TieredCompanyPricingOptionDTO> = yield call(requestOnboardingContractRateOptions);

    const { data } = response;

    yield put(setOnboardingContractRateOptions(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setOnboardingApplicationError({ ...error?.response?.data }));
    } else {
      yield put(setOnboardingApplicationError({ ...error }));
    }
  }
}

export function* handleGetProducts(action: { type: typeof getProducts; payload: { organizationUuid: string; productType?: ProductTypeEnum } }) {
  try {
    const response: AxiosResponse<ProductDto[]> = yield call(requestGetProducts, action?.payload?.organizationUuid, action?.payload?.productType);
    const { data } = response;
    yield put(setProducts(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setOnboardingApplicationError({ ...error?.response?.data }));
    } else {
      yield put(setOnboardingApplicationError({ ...error }));
    }
  }
}

export function* handleGetInosioTermsAndConditions(action: { type: typeof getInosioTermsAndConditions }) {
  try {
    const response: AxiosResponse<TermsAndConditionsDto> = yield call(requestInosioTermsAndConditions);
    const { data } = response;
    yield put(setInosioTermAndConditions(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setOnboardingApplicationError({ ...error?.response?.data }));
    } else {
      yield put(setOnboardingApplicationError({ ...error }));
    }
  }
}

export function* handleGetInosioPrivacyPolicy(action: { type: typeof getInosioPrivacyPolicy }) {
  try {
    const response: AxiosResponse<TermsAndConditionsDto> = yield call(requestInosioPrivacyPolicy);
    const { data } = response;
    yield put(setInosioPrivacyPolicy(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setOnboardingApplicationError({ ...error?.response?.data }));
    } else {
      yield put(setOnboardingApplicationError({ ...error }));
    }
  }
}

export function* handleGetPaysafeCCTermsAndConditions(action: { type: typeof getPaysafeCCTermsAndConditions }) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<TermsAndConditionsDto> = yield call(requestProcessorTermsAndConditions, currentUser.accessToken, currentUser.currentProfile.uuid);

    const { data } = response;
    yield put(setPaysafeCCTermAndConditions(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setOnboardingApplicationError({ ...error?.response?.data }));
    } else {
      yield put(setOnboardingApplicationError({ ...error }));
    }
  }
}

export function* handleGetRecordAgreementDetails(action: { type: typeof getRecordAgreementDetails; payload: AcceptTermsandConditionsDto }) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<void> = yield call(requestRecordAgreementDetails, currentUser.accessToken, action.payload);

    const { data } = response;
    yield put(setRecordAgreementDetailsSuccess(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setRecordAgreementDetailsFailed({ ...error?.response?.data }));
    } else {
      yield put(setRecordAgreementDetailsFailed({ ...error }));
    }
  }
}

export function* handleBatchRecordAgreementDetails(action: {
  type: typeof batchRecordAgreementDetails;
  payload: { firstPayload: AcceptTermsandConditionsDto; secondPayload: AcceptTermsandConditionsDto };
}) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);

    // Process the first payload
    const response1: AxiosResponse<void> = yield call(requestRecordAgreementDetails, currentUser.accessToken, action.payload.firstPayload);

    const { data: data1 } = response1;
    yield put(setRecordAgreementDetailsSuccess(data1));

    // Process the second payload
    const response2: AxiosResponse<void> = yield call(requestRecordAgreementDetails, currentUser.accessToken, action.payload.secondPayload);

    const { data: data2 } = response2;
    yield put(setRecordAgreementDetailsSuccess(data2));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setRecordAgreementDetailsFailed({ ...error?.response?.data }));
    } else {
      yield put(setRecordAgreementDetailsFailed({ ...error }));
    }
  }
}

export function* handleGetPmDetails(action: { type: typeof getRecordAgreementDetails }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);

    const response: AxiosResponse<CompanyStaffDto> = yield call(requestGetPmDetails, currentUser.accessToken, currentUser.currentProfile.uuid);

    const { data } = response;
    yield put(setPmDetails(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setPmDetailsError({ ...error?.response?.data }));
    } else {
      yield put(setPmDetailsError({ ...error }));
    }
  }
}

export function* handleFetchProductsPackagesV2(action: { type: typeof fetchProductsPackagesV2 }) {
  try {
    const response: AxiosResponse<ProductsPackagesV2Dto> = yield call(requestFetchProductsPackagesV2);

    const { data } = response;

    yield put(setProductsPackagesV2(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setErrorFetchingProductsPackagesV2({ ...error?.response?.data }));
    } else {
      yield put(setErrorFetchingProductsPackagesV2({ ...error }));
    }
  }
}

function* handleCreatePricingQuote(action: { type: typeof createPricingQuote; payload: UpsertPricingQuoteDto }) {
  try {
    const response: AxiosResponse<PricingQuoteDto> = yield call(requestCreatePricingQuote, action.payload);

    const { data } = response;
    yield put(setCreatePricingQuoteSuccess(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setCreatePricingQuoteFailed({ ...error?.response?.data }));
    } else {
      yield put(setCreatePricingQuoteFailed({ ...error }));
    }
  }
}

function* handleUpdatePricingQuote(action: { type: typeof updatePricingQuote; payload: { upsertPricingQuoteDto: UpsertPricingQuoteDto; pricingQuoteUuid: string } }) {
  try {
    const response: AxiosResponse<PricingQuoteDto> = yield call(requestUpdatePricingQuote, action.payload.pricingQuoteUuid, action.payload.upsertPricingQuoteDto);

    const { data } = response;
    yield put(setUpdatePricingQuoteSuccess(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setUpdatePricingQuoteFailed({ ...error?.response?.data }));
    } else {
      yield put(setUpdatePricingQuoteFailed({ ...error }));
    }
  }
}

function* handleFetchPricingQuote(action: { type: typeof fetchPricingQuote; payload: { pricingQuoteUuid: string } }) {
  try {
    const response: AxiosResponse<PricingQuoteDto> = yield call(requestFetchPricingQuote, action.payload.pricingQuoteUuid);

    const { data } = response;
    yield put(setFetchPricingQuoteSuccess(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setFetchPricingQuoteFailed({ ...error?.response?.data }));
    } else {
      yield put(setFetchPricingQuoteFailed({ ...error }));
    }
  }
}

function* handleDownloadPricingQuote(action: { type: typeof downloadPricingQuote; payload: { pricingQuoteUuid: string } }) {
  try {
    const response: AxiosResponse<Stream> = yield call(requestDownloadPricingQuote, action.payload.pricingQuoteUuid);

    let fileName = 'Pricing_Quote.pdf'; // just a fallback, actual file name comes from the API
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(response.headers['content-disposition']);
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, '');
    }
    const url = URL.createObjectURL(new Blob([response.data as unknown as BlobPart], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}`);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
    yield put(setDownloadPricingQuoteSuccess());
  } catch (error: any) {
    yield put(
      setDownloadPricingQuoteFailed({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

function* pmOnboardingSaga() {
  yield takeLatest(getUpdateOnboardingApplication.type, handleUpdateOnboardingApplication);
  yield takeLatest(getStartOnboardingApplication.type, handleStartOnboardingApplication);
  yield takeLatest(getOnboardingContractRateOptions.type, handleGetOnboardingContractRateOptions);
  yield takeLatest(getInosioTermsAndConditions.type, handleGetInosioTermsAndConditions);
  yield takeLatest(getInosioPrivacyPolicy.type, handleGetInosioPrivacyPolicy);
  yield takeLatest(getPaysafeCCTermsAndConditions.type, handleGetPaysafeCCTermsAndConditions);
  yield takeLatest(getRecordAgreementDetails.type, handleGetRecordAgreementDetails);
  yield takeLatest(getPmDetails.type, handleGetPmDetails);
  yield takeLatest(getProducts.type, handleGetProducts);
  yield takeLatest(batchRecordAgreementDetails.type, handleBatchRecordAgreementDetails);
  yield takeLatest(fetchProductsPackagesV2.type, handleFetchProductsPackagesV2);
  yield takeLatest(createPricingQuote.type, handleCreatePricingQuote);
  yield takeLatest(updatePricingQuote.type, handleUpdatePricingQuote);
  yield takeLatest(fetchPricingQuote.type, handleFetchPricingQuote);
  yield takeLatest(downloadPricingQuote.type, handleDownloadPricingQuote);
}

export default pmOnboardingSaga;
